import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface ErrorBoundaryProps {
  children: ReactNode
  /**
   * Cuando cambia este valor, el boundary se reinicia automáticamente. Pasar la ruta actual
   * para que, al navegar a otra página, un error previo no siga mostrándose.
   */
  resetKey?: string
  /** 'page' mantiene el layout alrededor; 'app' cubre toda la pantalla (fallo catastrófico). */
  variant?: 'page' | 'app'
}

interface ErrorBoundaryState {
  error: Error | null
}

/**
 * Aísla los errores de render de un subárbol: si una página falla, muestra un fallback en vez de
 * dejar la app en blanco. El resto de la navegación (menú, layout) sigue usable.
 */
export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log para diagnóstico; acá podría engancharse un servicio de monitoreo (Sentry, etc.).
    console.error('ErrorBoundary capturó un error de render:', error, info.componentStack)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  private handleReset = () => this.setState({ error: null })

  render() {
    if (!this.state.error) return this.props.children

    const isApp = this.props.variant === 'app'
    return (
      <div
        className={`flex flex-col items-center justify-center text-center p-6 ${
          isApp ? 'min-h-screen' : 'min-h-[60vh]'
        }`}
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100 text-red-600">
          <AlertTriangle className="h-7 w-7" aria-hidden="true" />
        </div>
        <h1 className="mt-4 text-lg font-semibold text-slate-800">Algo salió mal</h1>
        <p className="mt-1 max-w-md text-sm text-slate-500">
          Ocurrió un error inesperado al mostrar esta sección. Podés reintentar o volver a cargar la
          aplicación.
        </p>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          <button
            onClick={this.handleReset}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            Reintentar
          </button>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
          >
            Recargar la app
          </button>
        </div>
      </div>
    )
  }
}
